// frontend/src/services/avaService.js
// AVA AI Service - Frontend interface to AVA backend
// ===================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

/**
 * Send a message to AVA and get a response
 * 
 * @param {Object} params
 * @param {string} params.message - User's message
 * @param {Array} params.conversationHistory - Previous messages [{role, content}]
 * @param {string} params.currentPage - Current page path
 * @returns {Promise<{success: boolean, message: string, shouldEscalate?: boolean}>}
 */
export const sendMessage = async ({ message, conversationHistory = [], currentPage = null }) => {
  try {
    // Get auth token if available
    const token = localStorage.getItem('token');
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/ava/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message,
        conversationHistory,
        currentPage
      })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: data.message || "I'm having trouble connecting. Please try again.",
        error: data.error
      };
    }
    
    return data;
    
  } catch (error) {
    console.error('AVA service error:', error);
    
    // Return a friendly fallback message
    return {
      success: false,
      message: "I'm having trouble connecting right now. Please try again in a moment.",
      error: error.message
    };
  }
};

/**
 * Create a support ticket from AVA conversation
 * 
 * @param {Object} params
 * @param {string} params.conversationSummary - Summary of the conversation
 * @param {string} params.category - Ticket category
 * @param {string} params.priority - Ticket priority
 * @returns {Promise<{success: boolean, ticketId?: string, message?: string}>}
 */
export const escalateToSupport = async ({ conversationSummary, category = 'general', priority = 'medium' }) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return {
        success: false,
        error: 'You need to be logged in to create a support ticket.'
      };
    }
    
    const response = await fetch(`${API_BASE_URL}/ava/escalate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        conversationSummary,
        category,
        priority
      })
    });
    
    const data = await response.json();
    
    return data;
    
  } catch (error) {
    console.error('AVA escalation error:', error);
    return {
      success: false,
      error: 'Failed to create support ticket. Please try again.'
    };
  }
};

/**
 * Check if AVA service is available
 * @returns {Promise<boolean>}
 */
export const checkHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/ava/health`);
    const data = await response.json();
    return data.status === 'ok' && data.apiKeyConfigured;
  } catch (error) {
    console.error('AVA health check failed:', error);
    return false;
  }
};

/**
 * Format conversation for display
 * @param {Array} messages - Array of {role, content} objects
 * @returns {Array} Formatted messages with timestamps
 */
export const formatConversation = (messages) => {
  return messages.map((msg, index) => ({
    id: index,
    role: msg.role,
    content: msg.content,
    timestamp: msg.timestamp || new Date().toISOString()
  }));
};

/**
 * Summarize conversation for ticket escalation
 * @param {Array} messages - Array of {role, content} objects
 * @returns {string} Summary text
 */
export const summarizeConversation = (messages) => {
  if (!messages || messages.length === 0) {
    return 'No conversation history available.';
  }
  
  const summary = messages
    .map(msg => `${msg.role === 'user' ? 'User' : 'AVA'}: ${msg.content}`)
    .join('\n\n');
  
  return summary.slice(0, 2000); // Limit to 2000 chars
};

export default {
  sendMessage,
  escalateToSupport,
  checkHealth,
  formatConversation,
  summarizeConversation
};